import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, saveBlobAs } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Download, FileText, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

export default function ConsolidatedPage() {
  const [allSessions, setAllSessions] = useState([]);
  const [selected, setSelected] = useState(new Set()); // empty = all
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const sessions = await api.listSessions();
      setAllSessions(sessions);
      const ids = selected.size > 0 ? Array.from(selected) : null;
      const consolidated = await api.getConsolidated(ids);
      setData(consolidated);
    } catch (e) {
      toast.error("Failed to load consolidated report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };
  const selectAll = () => setSelected(new Set());
  const activeIds = selected.size > 0 ? Array.from(selected) : null;

  const refreshConsolidated = async () => {
    setLoading(true);
    try {
      const consolidated = await api.getConsolidated(activeIds);
      setData(consolidated);
    } catch {
      toast.error("Failed to refresh");
    } finally {
      setLoading(false);
    }
  };

  const download = async (kind) => {
    try {
      const res =
        kind === "pdf"
          ? await api.downloadConsolidatedPdf(activeIds)
          : await api.downloadConsolidatedCsv(activeIds);
      saveBlobAs(res.data, kind === "pdf" ? "uat-consolidated.pdf" : "uat-consolidated.csv");
    } catch (e) {
      toast.error(`Failed to export ${kind.toUpperCase()}`);
    }
  };

  const sectionsGrouped = useMemo(() => {
    if (!data) return [];
    const map = new Map();
    for (const c of data.cases) {
      const key = `${c.section_number}·${c.section_title}`;
      if (!map.has(key)) map.set(key, { title: c.section_title, number: c.section_number, cases: [] });
      map.get(key).cases.push(c);
    }
    return Array.from(map.values()).sort((a, b) => a.number - b.number);
  }, [data]);

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="overline mb-2 text-keplerio-teal">Consolidated Report</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-keplerio-navy">
            Roll up multiple UAT sessions
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Aggregate pass/fail counts across sessions per test case. Pick specific sessions
            below, or leave blank to include all.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            data-testid="consolidated-refresh-btn"
            onClick={refreshConsolidated}
            className="rounded-none border-slate-300 h-10"
          >
            <RefreshCcw size={14} className="mr-2" /> Refresh
          </Button>
          <Button
            variant="outline"
            data-testid="consolidated-export-csv-btn"
            onClick={() => download("csv")}
            className="rounded-none border-slate-300 h-10"
          >
            <Download size={14} className="mr-2" /> CSV
          </Button>
          <Button
            data-testid="consolidated-export-pdf-btn"
            onClick={() => download("pdf")}
            className="rounded-none bg-keplerio-navy hover:bg-keplerio-navy-dark text-white h-10"
          >
            <FileText size={14} className="mr-2" /> PDF report
          </Button>
        </div>
      </div>

      {/* Session picker */}
      <div className="bg-white border border-slate-200">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <div className="overline">Sessions included</div>
          <div className="text-xs text-slate-500">
            {selected.size === 0
              ? `Including all (${allSessions.length})`
              : `${selected.size} selected of ${allSessions.length}`}{" "}
            <button
              onClick={selectAll}
              data-testid="consolidated-select-all"
              className="ml-3 underline text-keplerio-teal hover:text-keplerio-teal-dark"
            >
              Include all
            </button>
          </div>
        </div>
        {allSessions.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">
            No sessions yet.{" "}
            <Link to="/new" className="text-keplerio-teal underline">
              Start one
            </Link>
          </div>
        ) : (
          <Table data-testid="consolidated-sessions-table">
            <TableHeader>
              <TableRow className="border-b border-slate-200">
                <TableHead className="w-10"></TableHead>
                <TableHead className="overline text-slate-500">Tester</TableHead>
                <TableHead className="overline text-slate-500">Browser · Device</TableHead>
                <TableHead className="overline text-slate-500">Started</TableHead>
                <TableHead className="overline text-slate-500">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {allSessions.map((s) => (
                <TableRow
                  key={s.id}
                  className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                  onClick={() => toggle(s.id)}
                >
                  <TableCell>
                    <Checkbox
                      checked={selected.has(s.id)}
                      data-testid={`consolidated-select-${s.id}`}
                      onCheckedChange={() => toggle(s.id)}
                      className="rounded-none border-slate-300 data-[state=checked]:bg-keplerio-teal data-[state=checked]:border-keplerio-teal"
                    />
                  </TableCell>
                  <TableCell className="text-sm text-slate-900 font-medium">
                    {s.tester_name}
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">
                    {s.browser || "—"} · {s.device || "—"}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500 font-mono">
                    {new Date(s.started_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-xs">{s.status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Totals */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <MetricBlock label="Sessions" value={data.session_count || 0} />
          <MetricBlock label="Passed" value={data.totals?.pass || 0} tone="pass" />
          <MetricBlock label="Failed" value={data.totals?.fail || 0} tone="fail" />
          <MetricBlock label="Skipped" value={data.totals?.skipped || 0} />
          <MetricBlock label="Blocked" value={data.totals?.blocked || 0} tone="block" />
        </div>
      )}

      {/* Per-case aggregation */}
      {loading ? (
        <div className="p-10 text-center text-slate-500">Loading…</div>
      ) : (
        sectionsGrouped.map((section) => (
          <section key={section.number} className="space-y-3">
            <div className="flex items-baseline gap-3">
              <span className="font-mono text-slate-400 text-sm">
                Section {section.number}
              </span>
              <h2 className="font-heading text-xl font-semibold tracking-tight text-keplerio-navy">
                {section.title}
              </h2>
            </div>
            <div className="bg-white border border-slate-200">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-slate-200">
                    <TableHead className="overline text-slate-500 w-[110px]">
                      Case
                    </TableHead>
                    <TableHead className="overline text-slate-500">Title</TableHead>
                    <TableHead className="overline text-slate-500 w-[60px] text-right">
                      Pass
                    </TableHead>
                    <TableHead className="overline text-slate-500 w-[60px] text-right">
                      Fail
                    </TableHead>
                    <TableHead className="overline text-slate-500 w-[60px] text-right">
                      Skip
                    </TableHead>
                    <TableHead className="overline text-slate-500 w-[60px] text-right">
                      Block
                    </TableHead>
                    <TableHead className="overline text-slate-500 w-[70px] text-right">
                      Not run
                    </TableHead>
                    <TableHead className="overline text-slate-500">
                      Failing testers
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {section.cases.map((c) => (
                    <TableRow
                      key={c.id}
                      data-testid={`consolidated-case-${c.id}`}
                      className="border-b border-slate-100"
                    >
                      <TableCell className="font-mono text-xs">
                        {c.id}
                        {c.is_regression && (
                          <Badge className="ml-1 rounded-none bg-keplerio-gold text-keplerio-navy-dark font-mono text-[9px] tracking-widest">
                            REG
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-800">{c.title}</TableCell>
                      <NumCell val={c.counts.pass} color="text-emerald-700" />
                      <NumCell val={c.counts.fail} color="text-red-700" bold />
                      <NumCell val={c.counts.skipped} color="text-slate-500" />
                      <NumCell val={c.counts.blocked} color="text-amber-700" />
                      <NumCell val={c.counts.not_run} color="text-slate-400" />
                      <TableCell className="text-xs text-slate-600">
                        {c.failing_testers.length > 0
                          ? c.failing_testers.join(", ")
                          : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </section>
        ))
      )}
    </div>
  );
}

function MetricBlock({ label, value, tone }) {
  const toneCls = {
    pass: "text-emerald-700",
    fail: "text-red-700",
    block: "text-amber-700",
  }[tone] || "text-keplerio-navy";
  return (
    <div className="bg-white border border-slate-200 p-4">
      <div className={`font-heading text-3xl font-bold tracking-tight ${toneCls}`}>
        {value}
      </div>
      <div className="overline mt-1">{label}</div>
    </div>
  );
}

function NumCell({ val, color, bold }) {
  return (
    <TableCell
      className={`text-right font-mono text-sm ${color} ${bold ? "font-bold" : ""}`}
    >
      {val}
    </TableCell>
  );
}
